import { useEffect, useRef } from 'react';
import styles from './equityCurve.module.scss';

export type EquityPoint = { time: number; pnl: number };

type EquityCurveProps = {
  points: EquityPoint[];
  variant: 'profit' | 'loss';
  animateOnMount?: boolean;
};

const PAD = { top: 8, bottom: 8, left: 8, right: 8 };
const ANIM_DURATION_MS = 900;

export const EquityCurve = ({ points, variant, animateOnMount = true }: EquityCurveProps): React.JSX.Element => {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const hasAnimatedRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;

    if (!canvas || !wrap) return;

    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    const styleVarName = variant === 'profit' ? '--textSuccessPrimary' : '--textDangerPrimary';

    const sorted = [...points].sort((pointLeft, pointRight) => pointLeft.time - pointRight.time);

    const draw = (): void => {
      const dpr = window.devicePixelRatio || 1;
      const W = wrap.clientWidth;
      const H = wrap.clientHeight;

      if (W === 0 || H === 0) return;

      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const color = getCssVar(canvas, styleVarName);

      if (!color) return;

      const fade = (alpha: number): string => `color-mix(in oklab, ${color} ${alpha * 100}%, transparent)`;

      ctx.clearRect(0, 0, W, H);

      if (sorted.length < 2) return;

      const xs = makeXScale(sorted, W);
      const ys = makeYScale(sorted, H);

      const animate = animateOnMount && !hasAnimatedRef.current;
      hasAnimatedRef.current = true;

      if (!animate) {
        renderCurve(ctx, sorted, xs, ys, color, fade, W, H, 1);

        return;
      }

      const start = performance.now();
      const step = (now: number): void => {
        const progress = Math.min((now - start) / ANIM_DURATION_MS, 1);
        const eased = 1 - Math.pow(1 - progress, 3);

        ctx.clearRect(0, 0, W, H);
        renderCurve(ctx, sorted, xs, ys, color, fade, W, H, eased);

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          rafRef.current = null;
        }
      };
      rafRef.current = requestAnimationFrame(step);
    };

    draw();

    const ro = new ResizeObserver(() => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      draw();
    });
    ro.observe(wrap);

    return () => {
      ro.disconnect();

      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [points, variant, animateOnMount]);

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <canvas ref={canvasRef} className={styles.canvas} />
    </div>
  );
};

function makeXScale(points: EquityPoint[], W: number) {
  const tMin = points[0].time;
  const tMax = points[points.length - 1].time;
  const span = tMax - tMin || 1;
  const innerW = W - PAD.left - PAD.right;

  return (i: number) => PAD.left + ((points[i].time - tMin) / span) * innerW;
}

function makeYScale(points: EquityPoint[], H: number) {
  let min = points[0].pnl;
  let max = points[0].pnl;

  for (const point of points) {
    if (point.pnl < min) min = point.pnl;

    if (point.pnl > max) max = point.pnl;
  }

  const range = max - min || 1;
  const padded = range * 0.1;
  const lo = min - padded;
  const hi = max + padded;
  const innerH = H - PAD.top - PAD.bottom;

  return (value: number) => PAD.top + (1 - (value - lo) / (hi - lo)) * innerH;
}

function renderCurve(
  ctx: CanvasRenderingContext2D,
  points: EquityPoint[],
  xs: (i: number) => number,
  ys: (v: number) => number,
  color: string,
  fade: (a: number) => string,
  W: number,
  H: number,
  progress: number,
): void {
  const pointCount = points.length;
  const xEnd = PAD.left + progress * (W - PAD.left - PAD.right);

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, xEnd, H);
  ctx.clip();

  const grad = ctx.createLinearGradient(0, PAD.top, 0, H);
  grad.addColorStop(0, fade(0.22));
  grad.addColorStop(1, fade(0));

  ctx.beginPath();
  ctx.moveTo(xs(0), ys(points[0].pnl));

  for (let i = 1; i < pointCount; i++) {
    const cx = (xs(i - 1) + xs(i)) / 2;
    ctx.bezierCurveTo(cx, ys(points[i - 1].pnl), cx, ys(points[i].pnl), xs(i), ys(points[i].pnl));
  }

  ctx.lineTo(xs(pointCount - 1), H);
  ctx.lineTo(xs(0), H);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(xs(0), ys(points[0].pnl));

  for (let i = 1; i < pointCount; i++) {
    const cx = (xs(i - 1) + xs(i)) / 2;
    ctx.bezierCurveTo(cx, ys(points[i - 1].pnl), cx, ys(points[i].pnl), xs(i), ys(points[i].pnl));
  }

  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.restore();

  if (progress >= 1) {
    const ex = xs(pointCount - 1);
    const ey = ys(points[pointCount - 1].pnl);
    ctx.beginPath();
    ctx.arc(ex, ey, 3.5, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(ex, ey, 5.5, 0, Math.PI * 2);
    ctx.strokeStyle = fade(0.35);
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

function getCssVar(el: Element, name: string): string {
  return getComputedStyle(el).getPropertyValue(name).trim();
}
